import * as React from 'react';
import Box from '@mui/material/Box';
import MuiSlider from '@mui/material/Slider';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import Input from './Input';

interface SliderProps {
	label?: string;
	value?: number | number[] | null;
	onChange: (
		event: Event,
		value: number | number[],
		activeThumb: number
	) => void;
	min?: number;
	max?: number;
	step?: number | null;
	marks?: boolean | Array<{ value: number; label?: string }>;
	valueLabelDisplay?: 'on' | 'auto' | 'off';
	disabled?: boolean;
	className?: string;
	fullWidth?: boolean;
	minWidth?: number;
	orientation?: 'horizontal' | 'vertical';
	'aria-label'?: string;
	getAriaValueText?: (value: number, index: number) => string;
	showInput?: boolean;
	inputLabel?: string;
	onInputChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	inputValue?: string;
	clearable?: boolean;
	onClear?: () => void;
}

const Slider: React.FC<SliderProps> = ({
	label,
	value,
	onChange,
	min = 0,
	max = 100,
	step = 1,
	marks = false,
	valueLabelDisplay = 'off',
	disabled = false,
	className,
	fullWidth = true,
	minWidth = 120,
	orientation = 'horizontal',
	'aria-label': ariaLabel,
	getAriaValueText,
	showInput = false,
	inputLabel,
	onInputChange,
	inputValue,
	clearable = false,
	onClear,
	...rest
}) => {
	if (showInput) {
		const hasValue = value !== null && value !== undefined;
		const effectiveValue = hasValue ? value : min; // Use min as default when no value

		return (
			<Box
				sx={{
					minWidth,
					width: fullWidth ? '100%' : 'auto',
					position: 'relative',
				}}
				className={className}
			>
				{label && (
					<Typography variant="body2" sx={{ mb: 1 }}>
						{label}
					</Typography>
				)}
				<Grid container spacing={2} alignItems="center">
					<Grid item xs>
						<MuiSlider
							value={effectiveValue}
							onChange={onChange}
							min={min}
							max={max}
							step={step}
							marks={marks}
							valueLabelDisplay={valueLabelDisplay}
							disabled={disabled}
							orientation={orientation}
							aria-label={ariaLabel}
							getAriaValueText={getAriaValueText}
							{...rest}
						/>
					</Grid>
					<Grid item>
						<Input
							value={
								inputValue ||
								(hasValue
									? Array.isArray(value)
										? value[0] === 0
											? ''
											: value[0].toString()
										: value === 0
										? ''
										: value.toString()
									: '')
							}
							label={inputLabel || ''}
							onChange={onInputChange}
							type="number"
							inputProps={{
								min: min,
								max: max,
								step: step,
							}}
							disabled={disabled}
							size="small"
							style={{ width: 80 }}
							placeholder={
								(hasValue && value === 0) || !hasValue
									? ''
									: undefined
							}
						/>
					</Grid>
					{clearable && hasValue && onClear && value !== 0 && (
						<Grid item>
							<IconButton
								onClick={onClear}
								size="small"
								sx={{
									padding: '4px',
									color: (theme) =>
										theme.palette.text.secondary,
									'&:hover': {
										color: (theme) =>
											theme.palette.error.main,
									},
								}}
								title="Clear selection"
							>
								<ClearIcon fontSize="small" />
							</IconButton>
						</Grid>
					)}
				</Grid>
			</Box>
		);
	}

	const hasValue = value !== null && value !== undefined;
	const effectiveValue = hasValue ? value : min; // Use min as default when no value

	return (
		<Box
			sx={{
				minWidth,
				width: fullWidth ? '100%' : 'auto',
				position: 'relative',
			}}
			className={className}
		>
			{label && (
				<Typography variant="body2" sx={{ mb: 1 }}>
					{label}
				</Typography>
			)}
			<Box sx={{ display: 'flex', alignItems: 'center' }}>
				<MuiSlider
					value={effectiveValue}
					onChange={onChange}
					min={min}
					max={max}
					step={step}
					marks={marks}
					valueLabelDisplay={valueLabelDisplay}
					disabled={disabled}
					orientation={orientation}
					aria-label={ariaLabel}
					getAriaValueText={getAriaValueText}
					sx={{ flexGrow: 1 }}
					{...rest}
				/>
				{clearable && hasValue && onClear && value !== 0 && (
					<IconButton
						onClick={onClear}
						size="small"
						sx={{
							marginLeft: 1,
							padding: '4px',
							color: (theme) => theme.palette.text.secondary,
							'&:hover': {
								color: (theme) => theme.palette.error.main,
							},
						}}
						title="Clear selection"
					>
						<ClearIcon fontSize="small" />
					</IconButton>
				)}
			</Box>
		</Box>
	);
};

export default Slider;
